import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowUpRight, Flame, Star, Clock } from "lucide-react";
import { content } from "../lib/i18n";
import { useLang } from "../context/LanguageContext";

const PUBLIC = process.env.PUBLIC_URL || "";

const SLIDES = [
  {
    src: `${PUBLIC}/brand/hero-french-tacos.jpg`,
    alt: "French Tacos — wraps grillés, frites et sauces Big Boss",
    objectPosition: "center 35%",
  },
  {
    src: `${PUBLIC}/brand/hero-spread.jpg`,
    alt: "Galettes, frites, onion rings et sauces Big Boss",
    objectPosition: "center",
  },
];

const SLIDE_INTERVAL = 6000;

const Hero = () => {
  const { lang } = useLang();
  const fr = content.hero.fr;
  const en = content.hero.en;
  const data = lang === "en" ? en : fr;

  const [index, setIndex] = useState(0);

  useEffect(() => {
    const t = setInterval(() => {
      setIndex((i) => (i + 1) % SLIDES.length);
    }, SLIDE_INTERVAL);
    return () => clearInterval(t);
  }, []);

  return (
    <section
      id="top"
      data-testid="hero-section"
      className="relative min-h-[100svh] w-full overflow-hidden bg-black text-white"
    >
      {/* Slider background */}
      <div className="absolute inset-0 z-0" data-testid="hero-slider">
        <AnimatePresence mode="sync">
          <motion.img
            key={index}
            src={SLIDES[index].src}
            alt={SLIDES[index].alt}
            initial={{ opacity: 0, scale: 1.04 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0 }}
            transition={{ opacity: { duration: 1.0, ease: "easeOut" }, scale: { duration: 7.0, ease: "linear" } }}
            className="absolute inset-0 h-full w-full object-cover"
            style={{ objectPosition: SLIDES[index].objectPosition }}
            loading={index === 0 ? "eager" : "lazy"}
          />
        </AnimatePresence>
        {/* Dark overlay (~50% combined) */}
        <div
          className="absolute inset-0"
          style={{
            background:
              "linear-gradient(180deg, rgba(0,0,0,0.55) 0%, rgba(0,0,0,0.45) 35%, rgba(0,0,0,0.85) 100%)",
          }}
        />
      </div>

      {/* Content */}
      <div className="relative z-10 mx-auto max-w-[1320px] px-5 md:px-8 lg:px-12 pt-24 md:pt-32 pb-16 min-h-[100svh] flex flex-col items-center md:items-start text-center md:text-left">
        <motion.div
          initial={{ opacity: 0, y: 14 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, ease: "easeOut", delay: 0.15 }}
        >
          <span className="eyebrow-yellow" data-testid="hero-eyebrow">
            <Flame size={12} strokeWidth={2.5} />
            <span>{data.eyebrow}</span>
          </span>
        </motion.div>

        <motion.h1
          initial={{ opacity: 0, y: 22 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, ease: "easeOut", delay: 0.25 }}
          data-testid="hero-title"
          className="mt-6 md:mt-9 h-display text-white"
        >
          <span className="block text-[14vw] sm:text-[12vw] md:text-[10vw] lg:text-[9.5rem] leading-[0.85]">
            BIG BOSS
          </span>
          <span className="block text-[10vw] sm:text-[9vw] md:text-[7.5vw] lg:text-[7rem] leading-[0.85] mt-2 text-[var(--bb-yellow)]">
            SNACK BAR
          </span>
        </motion.h1>

        <motion.div
          initial={{ opacity: 0, y: 14 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, ease: "easeOut", delay: 0.4 }}
          className="mt-6 md:mt-8 max-w-2xl"
        >
          <p
            data-testid="hero-tagline"
            className="font-display text-xl sm:text-2xl md:text-3xl lg:text-4xl text-white leading-tight uppercase"
          >
            {data.tagline}
            <span className="block text-[var(--bb-yellow)] mt-1">
              {data.taglineAccent}
            </span>
          </p>
          <p className="mt-4 text-sm md:text-base text-white/85 max-w-xl mx-auto md:mx-0 leading-relaxed">
            {data.sub}
          </p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 14 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, ease: "easeOut", delay: 0.55 }}
          className="mt-8 md:mt-10 flex flex-col sm:flex-row items-stretch sm:items-center gap-3 sm:gap-4 w-full sm:w-auto"
        >
          <a
            href={content.externalLinks.order}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="hero-order-cta"
            className="bb-btn bb-btn-yellow bb-btn-lg bb-btn-mobile-full"
          >
            <Flame size={18} strokeWidth={2.5} />
            <span>{data.cta1}</span>
            <ArrowUpRight size={18} strokeWidth={2.5} />
          </a>
          <a
            href={content.externalLinks.reservation}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="hero-reserve-cta"
            className="bb-btn bb-btn-ghost bb-btn-lg bb-btn-mobile-full"
          >
            <span>{data.cta2}</span>
            <ArrowUpRight size={18} strokeWidth={2.5} />
          </a>
        </motion.div>

        {/* Slider indicators */}
        <div
          className="mt-10 md:mt-12 flex items-center gap-3"
          data-testid="hero-slider-dots"
          role="tablist"
          aria-label="Hero slides"
        >
          {SLIDES.map((_, i) => (
            <button
              key={i}
              type="button"
              onClick={() => setIndex(i)}
              data-testid={`hero-slide-dot-${i}`}
              aria-label={`Slide ${i + 1}`}
              aria-pressed={i === index}
              className={`h-1.5 rounded-full transition-all duration-400 ${
                i === index
                  ? "w-12 bg-[var(--bb-yellow)]"
                  : "w-6 bg-white/40 hover:bg-white/60"
              }`}
            />
          ))}
          <span className="ml-2 text-[10px] tracking-[0.3em] uppercase text-white/55 font-bold">
            {String(index + 1).padStart(2, "0")} / {String(SLIDES.length).padStart(2, "0")}
          </span>
        </div>

        {/* Stats row */}
        <motion.div
          initial={{ opacity: 0, y: 14 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, ease: "easeOut", delay: 0.7 }}
          className="mt-auto pt-10 md:pt-14 grid grid-cols-3 gap-3 md:gap-8 max-w-2xl w-full mx-auto md:mx-0"
        >
          <Stat value="4.8" label={lang === "en" ? "Local rating" : "Note locale"} icon="star" />
          <Stat value="20'" label={lang === "en" ? "Avg. prep" : "Préparation"} icon="time" />
          <Stat value="100%" label={lang === "en" ? "Fresh daily" : "Frais chaque jour"} icon="leaf" />
        </motion.div>
      </div>
    </section>
  );
};

const Stat = ({ value, label, icon }) => (
  <div className="flex items-center gap-2 md:gap-3">
    <div className="h-9 w-9 md:h-10 md:w-10 rounded-full bg-[var(--bb-yellow)] text-black flex items-center justify-center shrink-0">
      {icon === "star" ? (
        <Star size={16} strokeWidth={2.5} fill="currentColor" />
      ) : icon === "time" ? (
        <Clock size={16} strokeWidth={2.5} />
      ) : (
        <Flame size={16} strokeWidth={2.5} />
      )}
    </div>
    <div className="text-left">
      <div className="font-display text-lg md:text-2xl text-white leading-none">{value}</div>
      <div className="text-[9px] md:text-[11px] uppercase tracking-[0.18em] text-white/70 mt-1 font-bold">
        {label}
      </div>
    </div>
  </div>
);

export default Hero;
